import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Icon } from '@iconify/react'
import { ImageScratchCard } from '@/components/PurchaseScratchCard/ImageScratchCard'
import type { usePurchaseScratch } from '@/hooks/usePurchaseScratch'
import type { LandingSpinResult } from '@/lib/landing-roulette'
import { scratchError } from '@/lib/purchase-scratch'
import styles from '@/components/PurchaseScratchCard/PurchaseScratchCard.module.css'

type Props = {
  scratch: ReturnType<typeof usePurchaseScratch>
  onChanged: () => void
  onUseCoupon: (prize: LandingSpinResult) => Promise<void>
  onViewNumbers: () => void
  salesOpen: boolean
}

export function PurchaseScratchModal({
  scratch,
  onChanged,
  onUseCoupon,
  onViewNumbers,
  salesOpen,
}: Props) {
  const dialog = useRef<HTMLDialogElement>(null)
  const rewardDialog = useRef<HTMLDialogElement>(null)
  const [celebrate, setCelebrate] = useState(false)
  const [visuallyRevealedId, setVisuallyRevealedId] = useState<number | null>(null)
  const [using, setUsing] = useState(false)
  const [useError, setUseError] = useState('')
  const [rewardOpen, setRewardOpen] = useState(false)
  const { attempt, state, busy, error, open } = scratch
  const visuallyRevealed = Boolean(attempt && visuallyRevealedId === attempt.id)
  const imageOnly = Boolean(
    attempt && !visuallyRevealed && !attempt.revealed_at && !error
  )
  const loading = busy && (!attempt || Boolean(attempt.revealed_at))
  const minimal = loading || imageOnly
  useEffect(() => {
    const element = dialog.current
    if (open && element && !element.open) element.showModal()
    if (!open) {
      if (element?.open) element.close()
      setRewardOpen(false)
    }
  }, [open])
  useEffect(() => {
    setVisuallyRevealedId(null)
    setRewardOpen(false)
    setUseError('')
    setCelebrate(false)
  }, [attempt?.id])
  useEffect(() => {
    const element = rewardDialog.current
    if (rewardOpen && open && element && !element.open) element.showModal()
    if ((!rewardOpen || !open) && element?.open) element.close()
  }, [open, rewardOpen])
  async function reveal() {
    setVisuallyRevealedId(attempt?.id ?? null)
    const card = await scratch.reveal()
    if (card) {
      onChanged()
      if (card.won && dialog.current?.open) {
        setCelebrate(true)
        setRewardOpen(true)
      }
    }
  }
  async function handleUseCoupon() {
    if (!attempt || using) return
    setUsing(true)
    setUseError('')
    // Native modal top-layer must close before opening the existing checkout widget.
    rewardDialog.current?.close()
    dialog.current?.close()
    try {
      await onUseCoupon({
        position: 0,
        prize_type: 'coupon',
        prize_payload: attempt.prize.prize_payload,
      })
      scratch.close()
    } catch (err) {
      setUseError(scratchError(err))
      dialog.current?.showModal()
      rewardDialog.current?.showModal()
    } finally {
      setUsing(false)
    }
  }
  const prize = attempt?.prize
  return (
    <>
      <dialog
        ref={dialog}
        autoFocus
        tabIndex={-1}
        className={`${styles.modal} ${minimal ? styles.imageOnlyModal : ''}`}
        style={
          !loading && imageOnly && attempt
            ? {
                width: `min(540px, calc(100vw - 24px), calc((100dvh - 32px) * ${attempt.cover.width / attempt.cover.height}))`,
              }
            : undefined
        }
        aria-label={minimal ? 'Sua raspadinha' : undefined}
        aria-labelledby={minimal ? undefined : 'purchase-scratch-title'}
        onCancel={(e) => {
          e.preventDefault()
          scratch.close()
        }}
        onClick={(event) => {
          if (minimal && event.target === event.currentTarget) scratch.close()
        }}
      >
        <div className={minimal ? styles.imageOnlyBody : styles.modalBody}>
          {!minimal && (
            <header className={styles.modalHeader}>
              <div>
                <span className={styles.eyebrow}>SUA COMPRA GUARDOU UMA SURPRESA</span>
                <h2 id="purchase-scratch-title">
                  {attempt?.title || state?.title || 'Sua raspadinha'}
                </h2>
                <p>{attempt?.description || state?.description}</p>
              </div>
              <button
                type="button"
                className={styles.close}
                aria-label="Fechar raspadinha"
                onClick={scratch.close}
              >
                <Icon icon="mdi:close" />
              </button>
            </header>
          )}
          {loading ? (
            <div
              className={styles.centeredLoading}
              role="status"
              aria-label="Preparando sua raspadinha"
            >
              <span className={styles.loadingSpinner} aria-hidden />
            </div>
          ) : attempt ? (
            <>
              <ImageScratchCard
                key={attempt.id}
                cover={attempt.cover}
                result={attempt.result_image}
                area={attempt.area}
                revealed={visuallyRevealed || Boolean(attempt.revealed_at)}
                onReveal={() => void reveal()}
                imageOnly={imageOnly}
              />
              {!imageOnly && (
                <p className={styles.receipt}>
                  Raspadinha #{attempt.id} · Compra #{attempt.payment_id}
                </p>
              )}
              {busy && !imageOnly && <p role="status">Registrando a revelação…</p>}
              {visuallyRevealed && !attempt.revealed_at && !busy && (
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => void reveal()}
                >
                  Confirmar revelação
                </button>
              )}
              {attempt.revealed_at && (
                <div className={styles.modalFooter}>
                  {attempt.won && (
                    <button
                      type="button"
                      className={styles.primaryButton}
                      onClick={() => {
                        setCelebrate(false)
                        setRewardOpen(true)
                      }}
                    >
                      Ver meu prêmio
                    </button>
                  )}
                  {Boolean(state?.available) && (
                    <button
                      type="button"
                      className={styles.quietButton}
                      disabled={busy}
                      onClick={() => void scratch.start()}
                    >
                      Próxima raspadinha · {state?.available}
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className={styles.empty}>
              {state?.cover ? (
                <img
                  src={state.cover.url}
                  alt="Capa da raspadinha"
                  style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 14 }}
                />
              ) : (
                <Icon icon="mdi:ticket-confirmation-outline" />
              )}
              <p>{state?.message || 'Consulte suas raspadinhas disponíveis.'}</p>
              <strong>Nenhuma raspadinha disponível agora.</strong>
            </div>
          )}
          {error && (
            <div className={styles.error} role="alert">
              {error}
              <button
                type="button"
                className={styles.quietButton}
                disabled={busy}
                onClick={() =>
                  void (attempt && !attempt.revealed_at ? reveal() : scratch.start())
                }
              >
                Tentar novamente
              </button>
            </div>
          )}
          {!minimal && !busy && Boolean(state?.history.length) && (
            <details style={{ marginTop: 24 }}>
              <summary>Minhas últimas raspadinhas</summary>
              <div className={styles.historyList}>
                {state?.history.map((card) => (
                  <button
                    type="button"
                    className={styles.historyItem}
                    key={card.id}
                    onClick={() => scratch.view(card)}
                  >
                    <span>
                      #{card.id} ·{' '}
                      {card.won ? card.prize.prize_payload?.label : 'Sem prêmio'}
                    </span>
                    <small>Ver cartão</small>
                  </button>
                ))}
              </div>
            </details>
          )}
        </div>
      </dialog>
      <dialog
        ref={rewardDialog}
        className={styles.modal}
        aria-labelledby="purchase-scratch-reward-title"
        onCancel={(e) => {
          e.preventDefault()
          setRewardOpen(false)
        }}
      >
        <div className={styles.modalBody}>
          {celebrate && (
            <div className={styles.confetti} aria-hidden>
              {Array.from({ length: 24 }, (_, i) => (
                <i key={i} style={{ '--n': i } as CSSProperties} />
              ))}
            </div>
          )}
          <button
            type="button"
            className={styles.close}
            style={{ marginLeft: 'auto' }}
            aria-label="Fechar prêmio"
            onClick={() => setRewardOpen(false)}
          >
            <Icon icon="mdi:close" />
          </button>
          <div className={styles.reward}>
            <Icon
              icon={
                prize?.prize_type === 'coupon'
                  ? 'mdi:ticket-percent-outline'
                  : 'mdi:ticket-confirmation-outline'
              }
            />
            <span className={styles.eyebrow}>ESSA SURPRESA É SUA</span>
            <h2 id="purchase-scratch-reward-title">
              {prize?.prize_type === 'coupon'
                ? `${prize.prize_payload?.discount_percent}% de desconto!`
                : `+${prize?.prize_payload?.quantity} números da sorte!`}
            </h2>
            <p>
              {prize?.status === 'canceled'
                ? 'Este prêmio foi cancelado porque a compra de origem foi revertida.'
                : prize?.status === 'used'
                  ? 'Este prêmio já foi utilizado.'
                  : prize?.prize_type === 'coupon'
                    ? 'Seu cupom está guardado. Use agora em uma nova compra nesta campanha.'
                    : 'Seus números estão reservados e esperando por você na aba Números.'}
            </p>
            {prize?.prize_type === 'coupon' && <code>{prize.prize_payload?.code}</code>}
            <div className={styles.modalFooter}>
              {prize?.status === 'pending' &&
                (prize.prize_type === 'coupon' ? (
                  <button
                    type="button"
                    className={styles.primaryButton}
                    disabled={using || !salesOpen}
                    onClick={() => void handleUseCoupon()}
                  >
                    {using ? 'Abrindo compra…' : 'Usar agora'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={() => {
                      setRewardOpen(false)
                      scratch.close()
                      onViewNumbers()
                    }}
                  >
                    Resgatar agora
                  </button>
                ))}
            </div>
            {useError && (
              <p className={styles.error} role="alert">
                {useError}
              </p>
            )}
          </div>
        </div>
      </dialog>
    </>
  )
}
